import AnnouncementIcon from '@mui/icons-material/AnnouncementOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import DashboardIcon from '@mui/icons-material/SpaceDashboardOutlined';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooksOutlined';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import ContactPageOutlinedIcon from '@mui/icons-material/ContactPageOutlined';
import { MenuItem, MenuList } from '@openclassrooms/ui-web/navigation';
import Typography from '@classify/react-core-components/Typography';
import { SelectableItemList } from '@openclassrooms/ui-web/ui';
import useGetMatchPath from '@openclassrooms/routing/hooks/useGetMatchPath';
import { mergeRoutes, ROUTES, getPathName, getButtonLinkProps } from '@openclassrooms/routing';
import { translate } from '@openclassrooms/translations';
import { ImageCdn } from '@openclassrooms/images-cdn';
import Paper from '@mui/material/Paper';
import Box from '@classify/react-core-components/Box';
import Divider from '@classify/react-core-components/Divider';
import ProfileCompletion from '@classify/react-product-components/ProfileCompletion';
import { styled } from '@classify/react-core-components/styles';
import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';
import tutorIcon from './images/tutorIcon.svg';
import PromotionMenuItem from './PromotionMenuItem';
const DISPLAYABLE_NAME_MAX_LENGTH = 30;
const PATH = {
  CREATE_COURSE: '/my-courses',
  ADMINISTRATION: '/spaces/admin',
  ALERTS: '/alertes',
  NOTIFICATIONS: '/notifications'
};
const Root = styled(Paper)(({
  theme: {
    palette,
    spacing
  }
}) => ({
  minWidth: 360,
  maxWidth: {
    sm: 360
  },
  maxHeight: 'calc(100vh - 64px)',
  overflowY: 'auto',
  '& .MainHeaderMenu__profileAvatar': {
    marginRight: spacing(2)
  },
  '& .MainHeaderMenu__menuItem': {
    minWidth: 304,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  '& .MainHeaderMenu__divider': {
    '&&': {
      margin: 0
    }
  },
  '& .MainHeaderMenu__list': {
    outline: 0,
    paddingBottom: 0
  },
  '& .MainHeaderMenu__beta': {
    marginLeft: spacing(1),
    marginRight: spacing(0.5)
  },
  '& .MainHeaderMenu__sectionTitle': {
    margin: spacing(1, 1, 0, 2),
    display: 'inline-block',
    color: palette.text.secondary
  }
}));
export default function MainHeaderMenu({
  displayableName = '',
  notificationCount = 0,
  moderationNotificationCount = 0,
  canEditCourse = false,
  canSeeMentorDashboard = false,
  canSeeAdminDashboard = false,
  canSeeOrganizationDashboard = false,
  canSeeTutorDashboard = false,
  profileLink = '',
  profileCompletionRate = null,
  profileAvatar = <PersonIcon />,
  employerSpaceLink = '',
  canDisplayForumAlert = false,
  organizationName = '',
  onLogOut = () => {},
  ...otherProps
}) {
  const matchPath = useGetMatchPath();
  const {
    userId,
    clear: clearCurrentUserCache
  } = useCurrentUserContext();
  const menuItemProps = {
    component: 'a',
    className: 'MainHeaderMenu__menuItem',
    role: 'link',
    tabIndex: 0
  };
  const dividerProps = {
    role: 'separator',
    className: 'MainHeaderMenu__divider'
  };
  const dashboardItems = [{
    condition: canSeeMentorDashboard,
    menuItem: {
      label: translate('main_header.support.dashboard'),
      value: 'mentor',
      leftIcon: <PeopleIcon />,
      'data-event': `Navigation|Clicked|Mentor::${userId}`,
      'data-testid': 'menuMentorLink',
      ...getButtonLinkProps(ROUTES.SPACES.MENTOR.INDEX)
    },
    pathsWhereSelected: mergeRoutes(ROUTES.SPACES.MENTOR)
  }, {
    condition: canSeeTutorDashboard,
    menuItem: {
      label: translate('main_header.apprenticeship_manager.dashboard'),
      value: 'tutor',
      leftIcon: <ImageCdn src={tutorIcon} alt="" />,
      'data-event': `Navigation|Clicked|Tutor::${userId}`,
      'data-testid': 'menuTutorLink',
      ...getButtonLinkProps(ROUTES.SPACES.WORKPLACE_ADVISOR.INDEX)
    },
    pathsWhereSelected: mergeRoutes(ROUTES.SPACES.WORKPLACE_ADVISOR)
  }, {
    condition: canSeeOrganizationDashboard && Boolean(employerSpaceLink),
    menuItem: {
      label: organizationName,
      value: 'organization',
      leftIcon: <BusinessIcon />,
      'data-testid': 'menuOrganizationLink',
      ...getButtonLinkProps(employerSpaceLink)
    },
    pathsWhereSelected: mergeRoutes(ROUTES.ORGANIZATIONS).concat(mergeRoutes(ROUTES.TEAMS))
  }, {
    condition: true,
    menuItem: {
      label: translate('main_header.student.dashboard'),
      value: 'student',
      leftIcon: <PersonIcon />,
      'data-testid': 'menuStudentLink',
      ...getButtonLinkProps(ROUTES.SPACES.STUDENT.INDEX)
    },
    pathsWhereSelected: mergeRoutes(ROUTES.SPACES.STUDENT)
  }, {
    condition: canSeeAdminDashboard,
    menuItem: {
      label: translate('main_header.admin.dashboard'),
      value: 'admin',
      leftIcon: <DashboardIcon />,
      href: PATH.ADMINISTRATION,
      'data-testid': 'menuAdministrationLink'
    },
    pathsWhereSelected: mergeRoutes(ROUTES.ADMIN)
  }].filter(({
    condition
  }) => condition).map(({
    menuItem,
    pathsWhereSelected
  }) => ({
    variant: 'body',
    isSelected: pathsWhereSelected.some(matchPath),
    ...menuItem
  }));
  const hasOnlyOneDashboard = dashboardItems.length === 1;
  const hasSeveralDashboards = !hasOnlyOneDashboard && dashboardItems.length > 1;
  if (hasOnlyOneDashboard) {
    dashboardItems[0].label = translate('main_header.only_one_dashboard.dashboard');
    dashboardItems[0].leftIcon = <DashboardIcon />;
  }
  const profileAvatarIcon = <div className="MainHeaderMenu__profileAvatar">{profileAvatar}</div>;
  return <Root elevation={8} sx={{}}>
      <MenuList className="MainHeaderMenu__list" role="menu" tabIndex={0} component="nav" {...otherProps}>
        <MenuItem {...menuItemProps} href={profileLink} label={<strong>
              {displayableName.length > DISPLAYABLE_NAME_MAX_LENGTH ? `${displayableName.substring(0, DISPLAYABLE_NAME_MAX_LENGTH)}...` : displayableName}
            </strong>} icon={profileAvatarIcon} sx={{
        mb: 1
      }} />
        <Divider {...dividerProps} />
        <div className="MainHeaderMenu__menuItem">
          {hasSeveralDashboards && <Typography className="MainHeaderMenu__sectionTitle" variant="overline">
              {translate('main_header.dashboard')}
            </Typography>}
          <SelectableItemList listComponent="div" itemComponent="a" items={dashboardItems.map(({
          pathsWhereSelected: pathsWhereSelected_0,
          ...item
        }) => item)} canClickOnSelected />
          <PromotionMenuItem />
        </div>
        <Divider {...dividerProps} />

        <div className="MainHeaderMenu__menuItem">
          <SelectableItemList listComponent="div" itemComponent="a" data-event="Navigation|Redirection::Internal|Menu logged access user profil'" items={[{
          label: translate('main_header.profile'),
          value: 'profile',
          leftIcon: <ContactPageOutlinedIcon />,
          href: getPathName(ROUTES.PROFILE.VIEW_AS, {
            persona: 'recruiter'
          }),
          isSelected: matchPath(ROUTES.PROFILE.VIEW_AS),
          variant: 'body',
          'aria-label': translate('main_header.profile'),
          labelAdornment: Number(profileCompletionRate) < 1 && <Box sx={{
            ml: 1,
            display: 'flex',
            flexGrow: 1
          }}>
                    <ProfileCompletion completion={profileCompletionRate} translations={{
              label: ''
            }} variant="inline" />
                  </Box>
        }]} canClickOnSelected />

          <SelectableItemList listComponent="div" itemComponent="a" items={[{
          label: translate('main_header.account-settings'),
          value: 'account',
          leftIcon: <SettingsIcon />,
          href: getPathName(ROUTES.ACCOUNT.INDEX),
          isSelected: matchPath(ROUTES.ACCOUNT.INDEX),
          variant: 'body',
          'aria-label': translate('main_header.account-settings')
        }]} canClickOnSelected />

          {canEditCourse && <>
              <Divider {...dividerProps} />

              <SelectableItemList listComponent="div" itemComponent="a" items={[{
            label: translate('main_header.create_course'),
            value: 'my-courses',
            leftIcon: <LibraryBooksIcon />,
            href: PATH.CREATE_COURSE,
            isSelected: matchPath(PATH.CREATE_COURSE),
            variant: 'body'
          }]} canClickOnSelected />
            </>}

          <Divider {...dividerProps} />

          <SelectableItemList listComponent="div" itemComponent="a" items={[canDisplayForumAlert && {
          label: translate('main_header.moderation_notifications', {
            count: moderationNotificationCount
          }, moderationNotificationCount),
          value: 'moderation-notifications',
          leftIcon: <AnnouncementIcon />,
          href: PATH.ALERTS,
          isSelected: matchPath(PATH.ALERTS),
          variant: 'body'
        }, {
          label: translate('main_header.notifications', {
            count: notificationCount
          }, notificationCount),
          value: 'notifications',
          leftIcon: <NotificationsIcon />,
          href: PATH.NOTIFICATIONS,
          isSelected: matchPath(PATH.NOTIFICATIONS),
          variant: 'body'
        }]} canClickOnSelected />

          <Divider className="MainHeaderMenu__divider" />
          <SelectableItemList listComponent="div" itemComponent="a" items={[{
          label: translate('main_header.deconnection'),
          value: 'disconnection',
          leftIcon: <PowerSettingsNewIcon />,
          onClick: async e => {
            e.preventDefault();
            await clearCurrentUserCache();
            onLogOut();
          },
          variant: 'body'
        }]} canClickOnSelected />
        </div>
      </MenuList>
    </Root>;
}